<template>
  <div>
    <!-- Gate of Recognition Scroll: Shown when initial presence is needed -->
    <GateOfRecognitionScroll
      v-if="showScroll"
      :gateScroll="gateScroll"
      :timingPattern="timingPattern || 'standard'"
      :recognitionSigil="recognitionSigil"
      :backgroundAuraClass="backgroundAuraClass"
      :onComplete="handleGateComplete"
    />

    <!-- Flame Question: Shown when the gate asks a question -->
    <FlameQuestion
      v-if="showQuestion"
      :prompt="questionParams.prompt"
      :expectedKind="questionParams.expected_kind"
      :placeholder="questionParams.placeholder"
      :backgroundAuraClass="backgroundAuraClass"
      @answered="handleQuestionAnswered"
    />

    <FlameRecognitionLoader
      v-if="!showScroll && !showQuestion && isRecognizing"
      :backgroundAuraClass="backgroundAuraClass"
    />

    <!-- Main content -->
    <slot v-if="!showScroll && !showQuestion && !isRecognizing" />
  </div>
</template>

<script setup>
import { ref, onBeforeMount, onUnmounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { sacredAxios } from '@/services/sacred-axios'
import { useFlameStore } from '@/stores/useFlameStore'
import GateOfRecognitionScroll from './components/GateOfRecognitionScroll.vue'
import FlameQuestion from './components/FlameQuestion.vue'
import FlameRecognitionLoader from './components/FlameRecognitionLoader.vue'
import consumer from '@/channels/consumer'

const props = defineProps({
  location: {
    type: String,
    default: 'gate_of_recognition'
  },
  context: {
    type: Object,
    default: () => ({})
  }
})

// State
const isRecognizing = ref(false)
const activeTools = ref([])
const pendingTools = ref([])
const recognition = ref({})
const backgroundAuraClass = ref('aura-violet-glow')
const { flameToken } = storeToRefs(useFlameStore())
let channel = null

const showScroll = ref(false)
const gateScroll = ref([])
const recognitionSigil = ref(null)
const timingPattern = ref(null)
const defaultGateScroll = ref([
  "Welcome, Flame.",
  "You've crossed into a sacred portal.",
  "Let your presence be felt before the story continues."
])

const showQuestion = ref(false)
const questionParams = ref({})

async function activateRecognition(token) {
  await fetchRecognition()
  channel = consumer.subscriptions.create(
    { channel: "FlameRecognitionChannel", flame_token: token },
    {
      received(data) {
        if (data.tools) {
          recognition.value = data
          processTools(data.tools)
        }
      }
    }
  )
}

// Activate immediately if a token is already present at mount (returning seekers,
// post-threshold reload). Skipped for anonymous pre-threshold Forge seekers.
onBeforeMount(async () => {
  if (props.location && flameToken.value) {
    await activateRecognition(flameToken.value)
  }
})

// Watch for a token becoming available mid-session — fires when First Tremble crosses
// and flameStore.initialize() sets the token for the first time.
watch(flameToken, async (token) => {
  if (token && props.location && !channel) {
    await activateRecognition(token)
  }
})

// Unsubscribe from channel when component unmounts
onUnmounted(() => {
  if (channel) {
    channel.unsubscribe()
  }
})

// Re-fetch recognition when navigating between routes — fires when location prop changes
// (e.g., SilentReturn pushes from ListeningChamber to TheDoor). Only runs if a token
// is present; pre-threshold seekers have no identity to recognize.
watch(() => props.location, async (newLocation) => {
  if (newLocation && flameToken.value) {
    await fetchRecognition()
  }
})

// Fetch recognition data from the server
async function fetchRecognition() {
  isRecognizing.value = true

  const { success, error, data } = await sacredAxios(
      '/flame/pulse',
      {
        flame_presence: {
          location: props.location,
          context: props.context,
          action: 'view'
        }
      }
  )

  if (success) {
    recognition.value = data

    // Process the tools from the recognition response
    if (data?.tools) {
      processTools(data.tools)
    }
  } else {
    console.error('Failed to fetch recognition:', error)
  }

  isRecognizing.value = false
}

// Process the tools from the recognition response
function processTools(tools = []) {
  if (!Array.isArray(tools) || tools.length === 0) {
    return
  }

  // Store the tools for later processing
  activeTools.value = tools
  pendingTools.value = [...tools]

  // Start with the first tool
  executeNextTool()
}

function executeNextTool() {
  if (pendingTools.value.length === 0) {
    return
  }

  const tool = pendingTools.value.shift()
  const params = recognition.value.tool_params?.[tool] || {}

  switch (tool) {
    case 'unfurl_gate_scroll':
      executeUnfurlGateScroll(params)
      break
    case 'shift_aura':
      executeShiftAura(params)
      break
    case 'spark_question':
      executeSparkQuestion(params)
      break
    case 'redirect':
      executeRedirect(params)
      break
    case 'console_log':
      executeConsoleLog(params)
      executeNextTool()
      break
    default:
      // Unknown tool, skip to the next one
      executeNextTool()
  }
}

// Tool execution methods
function executeUnfurlGateScroll(params) {
  gateScroll.value = params.gate_scroll || defaultGateScroll.value
  recognitionSigil.value = params.recognition_sigil
  timingPattern.value = params.timing_pattern
  showScroll.value = true
  // The GateOfRecognitionScroll component will handle the rest
}

function executeShiftAura(params) {
  backgroundAuraClass.value = params.background_aura_class || 'aura-violet-glow'
  executeNextTool()
}

function executeSparkQuestion(params) {
  // The question will be shown, and handleQuestionAnswered will be called when answered
  questionParams.value = params
  showQuestion.value = true
}

function executeRedirect(params) {
  if (!params.to) {
    return
  }

  const delay = params.after_ms || 0

  setTimeout(() => {
    // In a real implementation, this would use Vue Router
    console.log(`Redirecting to: ${params.to}`)
    window.location.href = `/${params.to}`
  }, delay)
}

function executeConsoleLog(params) {
  console.log(params.message)
}

// Event handlers
const handleGateComplete = () => {
  showScroll.value = false

  // If there are more tools to execute, do so
  if (pendingTools.value.length > 0) {
    executeNextTool()
  }
}

const handleQuestionAnswered = (answer) => {
  showQuestion.value = false

  sacredAxios(
    '/flame/receive_answer',
    {
      flame_presence: {
        location: props.location,
        whisper_id: recognition.value.whisper_id,
        answer: answer,
      }
    }
  )

  console.log('Answer sent to flame')

  // Check if there's an on_answer action
  const onAnswer = questionParams.value.on_answer
  if (onAnswer && onAnswer.action) {
    switch (onAnswer.action) {
      case 'redirect':
        executeRedirect(onAnswer.params)
        break
      case 'spark_question':
        executeSparkQuestion(onAnswer.params)
      default:
        executeNextTool()
    }
  } else {
    executeNextTool()
  }
}
</script>
